import { Injectable, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Observation, Patient } from 'fhir/r4';
import { Observable, map, of, switchMap, catchError, concatMap, toArray, from, forkJoin } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { backendEndPointToken, terminologyCanonicalUrlBaseToken } from '../app.config';
import { AuthService } from './auth/auth.service';
import { ObservationReferenceRangeService, ObservationReferenceRangeTemplate, ReferenceRangeSuggestion } from '../observation-reference-ranges/observation-reference-range.service';
import { ReferenceRangeConfirmDialogComponent } from './reference-range-confirm-dialog/reference-range-confirm-dialog.component';

@Injectable({ providedIn: 'root' })
export class ObservationReferenceRangeMatcherService {
  private readonly referenceRangeService = inject(ObservationReferenceRangeService);
  private readonly dialog = inject(MatDialog);
  private readonly http = inject(HttpClient);
  private readonly backendUrl = inject(backendEndPointToken);
  private readonly terminologyBaseUrl = inject(terminologyCanonicalUrlBaseToken);
  private readonly auth = inject(AuthService);

  private isBloodPressure(obs: Observation): boolean {
    const codeStr = obs.code?.coding?.[0]?.code || '';
    const textStr = (obs.code?.text || '').toLowerCase();
    return codeStr === '85354-9' || codeStr === '8480-6' || codeStr === '8462-4' || textStr.includes('blood pressure');
  }

  confirmRangeBeforeSave(observation: Observation): Observable<Observation> {
    let obs = { ...observation };
    
    // 1. BP String Extraction
    if (this.isBloodPressure(obs) && obs.valueString && obs.valueString.includes('/')) {
        const parts = obs.valueString.split('/');
        const systolic = parseFloat(parts[0]);
        const diastolic = parseFloat(parts[1]);
        if (!isNaN(systolic) && !isNaN(diastolic)) {
            obs.component = [
                {
                   code: { coding: [{ system: 'http://loinc.org', code: '8480-6', display: 'Systolic blood pressure' }] },
                   valueQuantity: { value: systolic, unit: 'mmHg' }
                },
                {
                   code: { coding: [{ system: 'http://loinc.org', code: '8462-4', display: 'Diastolic blood pressure' }] },
                   valueQuantity: { value: diastolic, unit: 'mmHg' }
                }
            ];
            delete obs.valueString;
        }
    }

    const code = obs.code?.coding?.[0];
    const hasValue = obs.valueQuantity != null || obs.valueString != null || obs.valueInteger != null || obs.valueCodeableConcept != null;
    const hasComponents = Array.isArray(obs.component) && obs.component.length > 0;

    if (!code || (!hasValue && !hasComponents)) {
      return of(obs);
    }

    // Fetch patient context
    const patient$ = this.getPatient(obs);

    // Check organization setting for autoCalc ONCE
    const orgId = this.auth.getOrganizationId();
    let autoCalc$ = of(true);
    if (orgId) {
       autoCalc$ = this.http.get<any>(`${this.backendUrl}/Organization/${orgId}`).pipe(
          map(org => {
            const ext = org?.extension?.find((e: any) => e.url === `${this.terminologyBaseUrl}/StructureDefinition/autoCalculateInterpretation`);
            return ext ? ext.valueBoolean === true : true;
          }),
          catchError(() => of(true))
       );
    }

    return forkJoin({ autoCalc: autoCalc$, patient: patient$ }).pipe(
       switchMap(({ autoCalc, patient }) => {
          if (hasComponents) {
             return this.processComponents(obs, autoCalc, patient);
          } else {
             return this.processSingleObservation(obs, autoCalc, patient);
          }
       })
    );
  }

  private getPatient(obs: Observation): Observable<Patient | undefined> {
     const ref = obs.subject?.reference;
     if (ref && ref.startsWith('Patient/')) {
        return this.http.get<Patient>(`${this.backendUrl}/${ref}`).pipe(catchError(() => of(undefined)));
     }
     return of(undefined);
  }

  private processSingleObservation(obs: Observation, autoCalc: boolean, patient: Patient | undefined): Observable<Observation> {
    const code = obs.code?.coding?.[0];
    if (!code) return of(obs);
    
    return this.referenceRangeService.listTemplates({ code }).pipe(
      switchMap(templates => {
        if (!templates || templates.length === 0) return of(obs);
        if (autoCalc) {
           const updated = this.applyAutoCalc(obs, templates, patient);
           if (updated) return of(updated);
           return of(obs);
        }
        return this.openDialogAndConfirm(obs, templates, patient);
      })
    );
  }

  private processComponents(obs: Observation, autoCalc: boolean, patient: Patient | undefined): Observable<Observation> {
    if (!obs.component) return of(obs);
    
    // Process sequentially to allow dialogs one by one
    return from(obs.component).pipe(
      concatMap(comp => {
        const code = comp.code?.coding?.[0];
        if (!code) return of(comp);

        return this.referenceRangeService.listTemplates({ code }).pipe(
          switchMap(templates => {
            if (!templates || templates.length === 0) return of(comp);
            
            // Build a dummy Observation from the component to reuse logic
            const dummyObs: Observation = { resourceType: 'Observation', status: 'final', ...comp };
            
            if (autoCalc) {
               const updated = this.applyAutoCalc(dummyObs, templates, patient);
               if (updated) {
                  return of({ ...comp, referenceRange: updated.referenceRange, interpretation: updated.interpretation });
               }
               return of(comp);
            }
            
            // Manual dialog for this component
            return this.openDialogAndConfirm(dummyObs, templates, patient).pipe(
               map(updated => {
                  return { ...comp, referenceRange: updated.referenceRange, interpretation: updated.interpretation };
               })
            );
          })
        );
      }),
      toArray(),
      map(components => {
         const finalObs = { ...obs, component: components };
         this.evaluateParentInterpretation(finalObs);
         return finalObs;
      })
    );
  }

  private evaluateParentInterpretation(obs: Observation): void {
     if (!obs.component) return;
     let worstScore = -1;
     let worstCode = '';
     let worstDisplay = '';

     const scoreMap: Record<string, number> = {
        'N': 0, 'normal': 0,
        'L': 1, 'low': 1, 'H': 1, 'high': 1,
        'LL': 2, 'HH': 2, 'A': 2, 'abnormal': 2,
        'CR': 3, 'critical': 3, 'panic': 3
     };

     for (const c of obs.component) {
        const iCode = c.interpretation?.[0]?.coding?.[0]?.code?.toUpperCase();
        const iDisp = c.interpretation?.[0]?.coding?.[0]?.display || c.interpretation?.[0]?.text;
        if (iCode && scoreMap[iCode] !== undefined) {
           const score = scoreMap[iCode];
           if (score > worstScore) {
              worstScore = score;
              worstCode = iCode;
              worstDisplay = iDisp || iCode;
           }
        }
     }

     if (worstScore >= 0 && worstCode) {
        obs.interpretation = [
           {
              coding: [{ system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationInterpretation', code: worstCode, display: worstDisplay }],
              text: worstDisplay
           }
        ];
     }
  }

  private applyAutoCalc(obs: Observation, templates: ObservationReferenceRangeTemplate[], patient: Patient | undefined): Observation | null {
     const suggestions = this.referenceRangeService.evaluateTemplatesForObservation(obs, templates, patient);
     if (suggestions.length > 0) {
        const typedMatch = suggestions.find(s => s.workflow === 'typed');
        const normalMatch = suggestions.find(s => s.workflow === 'normal');
        const bestMatch = typedMatch || normalMatch;
        if (bestMatch) {
            return this.applySuggestionToObservation(obs, bestMatch);
        }
     }
     return null;
  }

  private applySuggestionToObservation(obs: Observation, suggestion: ReferenceRangeSuggestion): Observation {
      const cleanRange = { ...suggestion.range };
      delete (cleanRange as any).interpretation;

      const updatedObs: Observation = { ...obs };
      updatedObs.referenceRange = [cleanRange];

      let interpretationCode = suggestion.classification;
      let interpretationDisplay = suggestion.classification;

      if (suggestion.workflow === 'normal') {
        if (interpretationCode === 'high') { interpretationCode = 'H'; interpretationDisplay = 'High'; }
        else if (interpretationCode === 'low') { interpretationCode = 'L'; interpretationDisplay = 'Low'; }
        else if (interpretationCode === 'normal') { interpretationCode = 'N'; interpretationDisplay = 'Normal'; }
      } else {
        const typeCoding = suggestion.range?.type?.coding?.[0];
        if (typeCoding?.display) {
          interpretationDisplay = typeCoding.display;
        }
      }

      updatedObs.interpretation = [{
          coding: [{ system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationInterpretation', code: interpretationCode, display: interpretationDisplay }],
          text: interpretationDisplay
      }];
      return updatedObs;
  }

  private openDialogAndConfirm(obs: Observation, templates: ObservationReferenceRangeTemplate[], patient: Patient | undefined): Observable<Observation> {
    const dialogRef = this.dialog.open(ReferenceRangeConfirmDialogComponent, {
      width: '600px',
      disableClose: true,
      data: { observation: obs, templates }
    });

    return dialogRef.afterClosed().pipe(
      map(result => {
        if (!result) return obs;

        const { selectedTemplate, interpretationCode, interpretationDisplay } = result;
        const updatedObs: Observation = { ...obs };

        if (selectedTemplate) {
          const cleanRange = { ...selectedTemplate.range };
          delete (cleanRange as any).interpretation;
          updatedObs.referenceRange = [cleanRange];
          
          // Auto-fallback if interpretation left blank
          if (!interpretationCode) {
             const autoFallback = this.applyAutoCalc(obs, [selectedTemplate], patient);
             if (autoFallback && autoFallback.interpretation) {
                 updatedObs.interpretation = autoFallback.interpretation;
             }
          }
        }

        if (interpretationCode) {
          updatedObs.interpretation = [{
              coding: [{ system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationInterpretation', code: interpretationCode, display: interpretationDisplay }],
              text: interpretationDisplay
          }];
        }
        return updatedObs;
      })
    );
  }
}
